// What the person looked at recently, for ordering lists ("recent first").
//
// Promise: a target counts as seen after it has been visible for VISIT_DWELL_MS
// in a visible window; the server keeps the latest time only, so a visit never
// moves back. Other windows follow through `visit.recorded`.
import {
  type QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { useApi } from "../app/context";
import type { Api } from "../data/api";

export interface Visit {
  place: string;
  kind: string;
  targetId: string;
  visitedAt: string;
}

export const VISIT_DWELL_MS = 10_000;

export const visitsPrefix = ["me", "visits"] as const;
export const visitsKey = (place: string) => [...visitsPrefix, place] as const;

export const visitsQuery = (api: Api, place: string) =>
  queryOptions({
    queryKey: visitsKey(place),
    queryFn: async () =>
      (
        await api<{ items: Visit[] }>(
          `/api/me/visits?place=${encodeURIComponent(place)}`,
        )
      ).items,
  });

export function useVisits(place: string) {
  return useQuery(visitsQuery(useApi(), place));
}

/** Puts a visit into the cached list (newest first; never moves one back). */
export function applyVisit(qc: QueryClient, visit: Visit) {
  qc.setQueryData<Visit[]>(visitsKey(visit.place), (old) => {
    if (!old) return old;
    const prev = old.find(
      (v) => v.kind === visit.kind && v.targetId === visit.targetId,
    );
    if (prev && prev.visitedAt >= visit.visitedAt) return old;
    return [visit, ...old.filter((v) => v !== prev)];
  });
}

/** Records a visit after the target has been visible for a while. */
export function useRecordVisitOnDwell(kind: string, targetId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  // frontend-development: a write-only side effect (recording that something was seen).
  useEffect(() => {
    if (!targetId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const arm = () => {
      if (timer) clearTimeout(timer);
      timer =
        document.visibilityState === "visible"
          ? setTimeout(async () => {
              const visit = await api<Visit>(
                `/api/me/visits/${encodeURIComponent(kind)}/${encodeURIComponent(targetId)}`,
                { method: "PUT" },
              ).catch(() => null);
              if (visit) applyVisit(qc, visit);
            }, VISIT_DWELL_MS)
          : null;
    };
    arm();
    document.addEventListener("visibilitychange", arm);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", arm);
    };
  }, [api, qc, kind, targetId]);
}
